import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendUp, TrendDown, Trophy, ChartPie, Wallet, CurrencyDollar, Clock, User, MapPin, CalendarBlank, ShieldCheck } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Today';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const config = {};
      const token = localStorage.getItem('oh_token');
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/users/${id}/profile`, config),
        axios.get(`${API}/users/${id}/trades`, config),
      ]);
      setProfile(pRes.data);
      setTrades(tRes.data);
    } catch {
      navigate('/markets');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center bg-[var(--oh-bg)]">
      <div className="w-8 h-8 border-2 border-oh-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!profile) return null;

  const s = profile.stats;
  const pl = s.total_profit;
  const plColor = pl >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]';
  const isOwnProfile = currentUser?.id === id;

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 pb-12 bg-[var(--oh-bg)] grid-bg" data-testid="profile-page">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[var(--oh-text-secondary)] hover:text-white transition-colors mb-6 mt-4" data-testid="profile-back">
          <ArrowLeft size={18} /> Back
        </button>

        {/* Profile Header */}
        <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-6 md:p-8 mb-6 animate-fade-up" data-testid="profile-header">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#00FFA3] to-[#00B8FF] flex items-center justify-center text-black font-bold text-3xl font-heading flex-shrink-0">
              {profile.name[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-heading text-2xl sm:text-3xl text-[var(--oh-text)]" data-testid="profile-name">{profile.name}</h1>
                {profile.role === 'admin' && (
                  <span className="bg-[rgba(245,158,11,0.1)] text-[#F59E0B] text-xs px-2 py-0.5 rounded-full border border-[rgba(245,158,11,0.2)] flex items-center gap-1">
                    <ShieldCheck size={12} weight="fill" /> Admin
                  </span>
                )}
                {isOwnProfile && (
                  <span className="bg-[rgba(0,255,163,0.1)] text-[#00FFA3] text-xs px-2 py-0.5 rounded-full border border-[rgba(0,255,163,0.2)]">You</span>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-[var(--oh-text-muted)]">
                <span className="flex items-center gap-1"><MapPin size={14} /> {profile.country}</span>
                <span className="flex items-center gap-1"><CalendarBlank size={14} /> Joined {timeAgo(profile.created_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {[
            { label: 'Total Profit', value: `${pl >= 0 ? '+' : ''}$${pl.toFixed(2)}`, color: plColor, icon: pl >= 0 ? TrendUp : TrendDown },
            { label: 'Win Rate', value: `${s.win_rate}%`, color: s.win_rate >= 50 ? 'text-[#00FFA3]' : 'text-[var(--oh-text-secondary)]', icon: Trophy },
            { label: 'Total Trades', value: s.total_trades, color: 'text-white', icon: ChartPie },
            { label: 'Wins', value: s.wins, color: 'text-[#00FFA3]', icon: TrendUp },
            { label: 'Losses', value: s.losses, color: 'text-[#FF3B30]', icon: TrendDown },
            { label: 'Active', value: s.active_positions, color: 'text-[#00B8FF]', icon: Clock },
          ].map((stat, i) => (
            <div key={i} className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-4 animate-fade-up" style={{ animationDelay: `${0.03 * i}s` }}>
              <div className="flex items-center gap-1.5 mb-1">
                <stat.icon size={14} className="text-[var(--oh-text-secondary)]" />
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-[var(--oh-text-secondary)]">{stat.label}</span>
              </div>
              <p className={`font-mono text-lg font-bold ${stat.color}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Trade History */}
        <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="font-heading text-xl text-[var(--oh-text)] mb-4">Trade History <span className="text-[var(--oh-text-muted)] font-mono text-base">({trades.length})</span></h2>

          {trades.length === 0 ? (
            <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-12 text-center">
              <User size={48} className="mx-auto mb-4 text-[var(--oh-text-muted)]" />
              <p className="text-[var(--oh-text-secondary)] text-lg">No trades yet</p>
            </div>
          ) : (
            <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl overflow-hidden">
              <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-5 py-3 border-b border-[var(--oh-border)] text-xs uppercase tracking-[0.15em] font-bold text-[var(--oh-text-muted)]">
                <span>Market</span>
                <span className="text-right">Position</span>
                <span className="text-right">Amount</span>
                <span className="text-right">Status</span>
                <span className="text-right">P&L</span>
              </div>
              {trades.map((t) => {
                const posColor = t.position === 'yes' ? '#00FFA3' : '#00B8FF';
                const statusMap = { active: { label: 'Active', color: '#A1A1AA' }, won: { label: 'Won', color: '#00FFA3' }, lost: { label: 'Lost', color: '#FF3B30' }, sold: { label: 'Sold', color: '#F59E0B' } };
                const st = statusMap[t.status] || statusMap.active;
                const profit = t.profit ?? 0;
                const profitColor = profit >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]';
                return (
                  <div key={t.id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 sm:gap-4 px-5 py-4 border-b border-[var(--oh-border)] last:border-0 hover:bg-[var(--oh-bg-tertiary)] transition-colors items-center"
                    data-testid={`trade-row-${t.id}`}>
                    <Link to={`/market/${t.market_id}`} className="text-sm text-[var(--oh-text)] hover:text-[#00FFA3] transition-colors truncate">
                      {t.market_question || 'Unknown market'}
                    </Link>
                    <span className="text-xs font-bold uppercase text-right px-2 py-0.5 rounded-sm w-fit sm:w-auto" style={{ color: posColor, backgroundColor: `${posColor}15` }}>
                      {t.position}
                    </span>
                    <span className="font-mono text-sm text-[var(--oh-text-secondary)] text-right">${t.amount.toFixed(2)}</span>
                    <span className="text-xs font-bold uppercase text-right" style={{ color: st.color }}>{st.label}</span>
                    <span className={`font-mono text-sm text-right ${t.status === 'active' ? 'text-[var(--oh-text-muted)]' : profitColor}`}>
                      {t.status === 'active' ? '-' : `${profit >= 0 ? '+' : ''}$${profit.toFixed(2)}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
